import React from "react";
import { Controller, Control, FieldErrors } from "react-hook-form";
import { FormattedMessage, useIntl } from "react-intl";
import { Form } from "react-bootstrap";
import { LoyaltyBrand } from "../../../gen/witbooking/backoffice/loyalty/brand/v1/loyalty_brand_api_pb";
import { IPropertyLoyaltyBrand } from "../../../components/loyalty-brand-provider";
import Selector from "../../../components/selector";
import { messages } from "../messages";
import {
  Container,
  Label,
  StyledFormGroup,
  StyledFormControl,
  ErrorText,
  HelpText,
  TranslationWrapper,
} from "../styles";
import { BrandFormData, BrandFormInput, Option } from "../types";
import { BrandTranslationComponent } from "../components";

interface BrandFormFieldsProps {
  control: Control<BrandFormInput, unknown, BrandFormData>;
  errors: FieldErrors<BrandFormInput>;
  selectedBrand?: LoyaltyBrand;
  propertyBrand?: IPropertyLoyaltyBrand;
  propertyOptions: Option[];
  widgetLoyaltyWatch: boolean | undefined;
  backendTranslations?: Array<{ locale: string; name: string; description: string }>;
}

export const BrandFormFields: React.FC<BrandFormFieldsProps> = ({
  control,
  errors,
  selectedBrand,
  propertyBrand,
  propertyOptions,
  widgetLoyaltyWatch,
  backendTranslations,
}) => {
  const intl = useIntl();

  return (
    <Container>
      <div>
        <Label>
          <FormattedMessage {...messages.brandTickerLabel} />
        </Label>
        <StyledFormControl
          type="text"
          value={propertyBrand ? propertyBrand.ticker : ""}
          placeholder={propertyBrand ? propertyBrand.ticker : ""}
          disabled
        />
      </div>
      {selectedBrand && (
        <>
          <div>
            <Label>
              <FormattedMessage {...messages.brandNameLabel} />
            </Label>
            <TranslationWrapper>
              <BrandTranslationComponent defaultValues={backendTranslations} />
            </TranslationWrapper>
          </div>
          <div>
            <Label>
              <FormattedMessage {...messages.managedByLabel} />
            </Label>
            <Controller
              name="managedByOptions"
              control={control}
              render={({ field }) => (
                <Selector
                  isMulti
                  options={propertyOptions}
                  value={field.value}
                  onChange={(value: Option[] | null) => field.onChange(value || [])}
                  placeholder={<FormattedMessage {...messages.managedByPlaceholder} />}
                />
              )}
            />
          </div>
          <div>
            <Label>
              <FormattedMessage {...messages.usedByLabel} />
            </Label>
            <Controller
              name="usedByOptions"
              control={control}
              render={({ field }) => (
                <Selector
                  isMulti
                  options={propertyOptions}
                  value={field.value}
                  onChange={(value: Option[] | null) => field.onChange(value || [])}
                  placeholder={<FormattedMessage {...messages.usedByPlaceholder} />}
                />
              )}
            />
          </div>
          <StyledFormGroup>
            <Label>
              <FormattedMessage {...messages.emailReceiverLabel} />
            </Label>
            <Controller
              name="emailReceiver"
              control={control}
              rules={{
                required: true,
                pattern: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
              }}
              render={({ field }) => (
                <>
                  <StyledFormControl
                    {...field}
                    type="email"
                    hasError={!!errors.emailReceiver}
                    placeholder={intl.formatMessage(messages.emailReceiverLabel)}
                  />
                  {errors.emailReceiver && (
                    <ErrorText>
                      <FormattedMessage {...messages.invalidEmailError} />
                    </ErrorText>
                  )}
                </>
              )}
            />
          </StyledFormGroup>
          <StyledFormGroup>
            <Label>
              <FormattedMessage {...messages.emailSenderLabel} />
            </Label>
            <Controller
              name="emailSender"
              control={control}
              render={({ field }) => (
                <StyledFormControl {...field} type="email" disabled hasError={false} />
              )}
            />
          </StyledFormGroup>
          <StyledFormGroup>
            <Label>
              <FormattedMessage {...messages.replyToEmailLabel} />
            </Label>
            <Controller
              name="replyToEmail"
              control={control}
              rules={{
                pattern: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
              }}
              render={({ field }) => (
                <>
                  <StyledFormControl
                    {...field}
                    type="email"
                    hasError={!!errors.replyToEmail}
                    placeholder={intl.formatMessage(messages.replyToEmailLabel)}
                  />
                  {errors.replyToEmail && (
                    <ErrorText>
                      <FormattedMessage {...messages.invalidEmailError} />
                    </ErrorText>
                  )}
                </>
              )}
            />
          </StyledFormGroup>
          <StyledFormGroup>
            <Label>
              <FormattedMessage {...messages.privacyPolicyLabel} />
            </Label>
            <Controller
              name="privacyPolicy"
              control={control}
              rules={{ required: true }}
              render={({ field }) => (
                <>
                  <StyledFormControl
                    {...field}
                    type="url"
                    hasError={!!errors.privacyPolicy}
                    placeholder={intl.formatMessage(messages.privacyPolicyLabel)}
                  />
                  {errors.privacyPolicy && (
                    <ErrorText>
                      <FormattedMessage {...messages.emptyFieldError} />
                    </ErrorText>
                  )}
                </>
              )}
            />
          </StyledFormGroup>
          <StyledFormGroup>
            <Controller
              name="enableEmailChange"
              control={control}
              render={({ field }) => (
                <Form.Check
                  type="checkbox"
                  label={<FormattedMessage {...messages.enableEmailChangeLabel} />}
                  checked={field.value}
                  onChange={field.onChange}
                />
              )}
            />
            <HelpText>
              <FormattedMessage {...messages.enableEmailChangeHelp} />
            </HelpText>
          </StyledFormGroup>
          <StyledFormGroup>
            <Controller
              name="sendWelcomeEmail"
              control={control}
              render={({ field }) => (
                <Form.Check
                  type="checkbox"
                  label={<FormattedMessage {...messages.sendWelcomeEmailLabel} />}
                  checked={field.value}
                  onChange={field.onChange}
                />
              )}
            />
            <HelpText>
              <FormattedMessage {...messages.sendWelcomeEmailHelp} />
            </HelpText>
          </StyledFormGroup>
          <StyledFormGroup>
            <Controller
              name="useExternalAuthSystem"
              control={control}
              render={({ field }) => (
                <Form.Check
                  type="checkbox"
                  label={<FormattedMessage {...messages.useExternalAuthSystemLabel} />}
                  checked={field.value}
                  onChange={field.onChange}
                />
              )}
            />
          </StyledFormGroup>
          <StyledFormGroup>
            <Controller
              name="widgetLoyalty"
              control={control}
              render={({ field }) => (
                <Form.Check
                  type="checkbox"
                  label={<FormattedMessage {...messages.widgetLoyaltyLabel} />}
                  checked={field.value}
                  onChange={field.onChange}
                />
              )}
            />
            {errors.widgetLoyalty && (
              <ErrorText>
                <FormattedMessage {...messages.widgetLoyaltyValidationError} />
              </ErrorText>
            )}
          </StyledFormGroup>
          {widgetLoyaltyWatch && (
            <>
              <StyledFormGroup>
                <Label>
                  <FormattedMessage {...messages.widgetUrlLabel} /> *
                </Label>
                <Controller
                  name="widgetUrl"
                  control={control}
                  render={({ field }) => (
                    <>
                      <StyledFormControl
                        {...field}
                        type="url"
                        hasError={!!errors.widgetUrl}
                        placeholder={intl.formatMessage(messages.widgetUrlLabel)}
                      />
                      <HelpText>
                        <FormattedMessage {...messages.widgetUrlHelp} />
                      </HelpText>
                      {errors.widgetUrl && (
                        <ErrorText>
                          <FormattedMessage {...messages.emptyFieldError} />
                        </ErrorText>
                      )}
                    </>
                  )}
                />
              </StyledFormGroup>
              <StyledFormGroup>
                <Label>
                  <FormattedMessage {...messages.idTierAgencyLabel} /> *
                </Label>
                <Controller
                  name="idTierAgency"
                  control={control}
                  render={({ field }) => (
                    <>
                      <StyledFormControl
                        {...field}
                        type="text"
                        hasError={!!errors.idTierAgency}
                        placeholder={intl.formatMessage(messages.idTierAgencyLabel)}
                      />
                      {errors.idTierAgency && (
                        <ErrorText>
                          <FormattedMessage {...messages.emptyFieldError} />
                        </ErrorText>
                      )}
                    </>
                  )}
                />
              </StyledFormGroup>
              <StyledFormGroup>
                <Label>
                  <FormattedMessage {...messages.idTierCompanyLabel} /> *
                </Label>
                <Controller
                  name="idTierCompany"
                  control={control}
                  render={({ field }) => (
                    <>
                      <StyledFormControl
                        {...field}
                        type="text"
                        hasError={!!errors.idTierCompany}
                        placeholder={intl.formatMessage(messages.idTierCompanyLabel)}
                      />
                      {errors.idTierCompany && (
                        <ErrorText>
                          <FormattedMessage {...messages.emptyFieldError} />
                        </ErrorText>
                      )}
                    </>
                  )}
                />
              </StyledFormGroup>
            </>
          )}
        </>
      )}
    </Container>
  );
};
